import './product-card.component.scss';

import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import type { BuyItNowRequest } from '../../../cart/models/cart.model';
import { useShopifyCurrency } from '../../../common/currency/shopify-currency.hook';
import { IconCaret } from '../../../common/icons/icon-caret';
import { useTranslation } from '../../../i18n';
import type { Product } from '../../../shopify/models/product.model';
import { useAppDispatch } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';
import { buyItNow } from '../../store/content.thunk';

export interface ProductCardComponentProps {
  product: Product;
  productTitleSize?: string;
  showProductTitle?: boolean;
  showProductPrice?: boolean;
  showQuickAdd?: boolean;
  separateTab?: boolean;
  onBuyItNow?: (variantId: string) => void;
  currencyCode?: string; // e.g., 'USD'
  locale?: string; // e.g., 'en-US'
  key?: string;
}

export function ProductCard({
  key,
  product,
  productTitleSize = '',
  showProductTitle = true,
  showProductPrice = true,
  showQuickAdd = true,
  separateTab = true,
  onBuyItNow,
  currencyCode = 'USD',
  locale = 'en-US',
}: ProductCardComponentProps) {
  const dispatch = useAppDispatch();
  const { shopifyCurrency } = useShopifyCurrency();
  const { t } = useTranslation();

  if (!product) return null;

  // Available variants only (for the selector)
  const availableVariants = useMemo(
    () => product.variants?.filter((v) => v.available) ?? [],
    [product.variants]
  );

  // Selected variant state (default to first variant if present)
  const [selectedVariantId, setSelectedVariantId] = useState<string | number>('');

  useEffect(() => {
    if (product?.variants?.length > 0) {
      setSelectedVariantId(product.variants[0].id);
    } else {
      setSelectedVariantId('');
    }
  }, [product?.id, product?.variants]);

  const goToProduct = (handle: string) => {
    const target = separateTab ? '_blank' : '_self';
    window.open(`/products/${handle}`, target);
  };

  const handleBuyItNow = async () => {
    const variants = product.variants ?? [];
    const variantId = variants.length === 1 ? variants[0].id : selectedVariantId;

    if (!variantId) {
      return;
    }

    if (onBuyItNow) {
      onBuyItNow(variantId as string);
    } else {
      const checkoutWindow = separateTab ? window.open('', '_blank') : null;
      const result = await dispatch(
        buyItNow({
          productVariantId: variantId,
          product,
        } as BuyItNowRequest)
      );

      if (buyItNow.fulfilled.match(result)) {
        if (separateTab && checkoutWindow) {
          checkoutWindow.location.href = '/checkout';
        } else {
          window.location.href = '/checkout';
        }
      } else if (buyItNow.rejected.match(result)) {
        checkoutWindow?.close();
        console.error('[ProductCard] buyItNow failed:', result);
        toastService.error(t('common.error'), t('product.errorAddToCart'));
      }
    }
  };

  const hasImage = !!product.featured_image;

  return (
    <div class="single-product-card">
      <div
        class="single-card-image-wrapper"
        onClick={() => goToProduct(product.handle)}
        role="button"
        tabIndex={0}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') goToProduct(product.handle);
        }}
      >
        <div
          class={`single-card-image ${hasImage ? '' : 'no-image'}`}
          style={{
            backgroundImage: hasImage ? `url(${product.featured_image})` : 'none',
          }}
          aria-label={product.title}
        />
      </div>

      <div class="single-card-inner">
        <div class="single-card-title-price">
          {showProductTitle && (
            <h3 class={`single-collection-product-title card__heading ${productTitleSize}`}>
              {product.title}
            </h3>
          )}

          {/* Single variant price */}
          {product.variants?.length === 1 && showProductPrice && (
            <p class="single-collection-product-price" id={`single-product-price-${product.id}`}>
              {shopifyCurrency(product.variants[0].price)}
            </p>
          )}
        </div>

        <div class="single-card-buy-button">
          {/* Variant selector (when more than one variant) */}
          {showQuickAdd && product.variants?.length > 1 && (
            <div class="select single-select-wrap single-variant-select">
              <select
                class="single-select select__select"
                id={`single-variant-select-${product.id}`}
                value={selectedVariantId}
                onChange={(e: any) => setSelectedVariantId(e.currentTarget.value)}
              >
                {availableVariants.map((variant) => (
                  <option key={variant.id} value={variant.id}>
                    {variant.title}
                    {showProductPrice ? ` - ${shopifyCurrency(variant.price)}` : ''}
                  </option>
                ))}
              </select>
              <IconCaret />
            </div>
          )}

          {showQuickAdd && (
            <button
              class="btn button button--full-width"
              disabled={!product.available}
              onClick={handleBuyItNow}
            >
              {product.available ? t('product.buyNow') : t('product.soldOut')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
